import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/index.css';
import { Toaster } from '@/components/ui/sonner';

import Login from './pages/Login';
import TeacherDashboard from './pages/TeacherDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Profile from './pages/Profile';
import Settings from './pages/Settings';
import LessonPlans from './pages/LessonPlans';
import LessonPlanBuilder from './pages/LessonPlanBuilder';
import LessonPlanView from './pages/LessonPlanView';
import Subscription from './pages/Subscription';
import Assessments from './pages/Assessments';
import CreateQuiz from './pages/CreateQuiz';
import CreateWorksheet from './pages/CreateWorksheet';
import ViewQuiz from './pages/ViewQuiz';
import ViewWorksheet from './pages/ViewWorksheet';
import QuestionBank from './pages/QuestionBank';
import LearnerProfiles from './pages/LearnerProfiles';
import EmailVerification from './pages/EmailVerification';
import ResetPassword from './pages/ResetPassword';
import { AuthProvider, useAuth } from './context/AuthContext';

const PrivateRoute = ({ children, role }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-sky-50">
        <div className="glass-card p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (role && user.role !== role) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/dashboard'} replace />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          
          <Route path="/dashboard" element={
            <PrivateRoute role="teacher">
              <TeacherDashboard />
            </PrivateRoute>
          } />
          
          <Route path="/profile" element={
            <PrivateRoute role="teacher">
              <Profile />
            </PrivateRoute>
          } />
          
          <Route path="/settings" element={
            <PrivateRoute role="teacher">
              <Settings />
            </PrivateRoute>
          } />
          
          <Route path="/lesson-plans" element={
            <PrivateRoute role="teacher">
              <LessonPlans />
            </PrivateRoute>
          } />
          
          <Route path="/lesson-plans/new" element={
            <PrivateRoute role="teacher">
              <LessonPlanBuilder />
            </PrivateRoute>
          } />
          
          <Route path="/lesson-plans/:planId" element={
            <PrivateRoute role="teacher">
              <LessonPlanView />
            </PrivateRoute>
          } />
          
          <Route path="/subscription" element={
            <PrivateRoute role="teacher">
              <Subscription />
            </PrivateRoute>
          } />
          
          <Route path="/assessments" element={
            <PrivateRoute role="teacher">
              <Assessments />
            </PrivateRoute>
          } />
          
          <Route path="/assessments/quiz/new" element={
            <PrivateRoute role="teacher">
              <CreateQuiz />
            </PrivateRoute>
          } />
          
          <Route path="/assessments/quiz/:quizId" element={
            <PrivateRoute role="teacher">
              <ViewQuiz />
            </PrivateRoute>
          } />
          
          <Route path="/assessments/worksheet/new" element={
            <PrivateRoute role="teacher">
              <CreateWorksheet />
            </PrivateRoute>
          } />
          
          <Route path="/assessments/worksheet/:worksheetId" element={
            <PrivateRoute role="teacher">
              <ViewWorksheet />
            </PrivateRoute>
          } />
          
          <Route path="/assessments/question-bank" element={
            <PrivateRoute role="teacher">
              <QuestionBank />
            </PrivateRoute>
          } />
          
          <Route path="/learner-profiles" element={
            <PrivateRoute role="teacher">
              <LearnerProfiles />
            </PrivateRoute>
          } />
          
          <Route path="/verify-email" element={
            <PrivateRoute role="teacher">
              <EmailVerification />
            </PrivateRoute>
          } />
          
          <Route path="/admin" element={
            <PrivateRoute role="admin">
              <AdminDashboard />
            </PrivateRoute>
          } />
          
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;